import { useState, useEffect } from 'react'
import Header from 'components/Dashboard/Header'
import NextAppointment from 'components/Dashboard/NextAppointment'
import Profile from 'components/Dashboard/Profile'
import { ProfileSkeleton, VisitsSkeleton } from 'components/Dashboard/Skeleton'
import ConfirmAppointmentModal from 'components/Appointment/ConfirmAppointmentModal'
import AppointmentModal from 'components/Appointment/AppointmentModal'
import {
  getUpcomingAppointments,
  findAppointment,
  arePatientFormsComplete,
} from 'components/Dashboard/helper'
import { AppointmentActions, DashboardActions, ProfileActions } from 'store/actions'
import { mapAppointment } from 'store/Appointments/helper'
import { getPractice } from 'library/auth'
import { connect } from 'react-redux'
import toast from 'react-hot-toast'


let enhancer = connect(null, { ...DashboardActions, ...AppointmentActions, ...ProfileActions })

const Dashboard = ({ showDashboard, showProfile, updateAppointment }) => {
  const [dashboardata, setDashboardData] = useState(null)
  const [profile, setProfile] = useState(null)
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const upcomingAppointments = getUpcomingAppointments(dashboardata)
  const selectedAppointment = findAppointment(upcomingAppointments, selectedAppointmentId)

  const appointment = selectedAppointment && mapAppointment(selectedAppointment.attributes)
  const formsAccess = Boolean(getPractice()?.patient_forms_access)

  const loadDashboard = async () => {
    try {
      const { data } = await showDashboard()
      setDashboardData(data)

      const [firstAppointment] = getUpcomingAppointments(data)
      setSelectedAppointmentId((id) => id ?? firstAppointment?.attributes?.encoded_id ?? null)
    } catch (error: any) {
      toast.error(error.errors || 'Failed to load dashboard. Please try again later.')
    }
  }

  const loadProfile = async () => {
    try {
      const { data } = await showProfile()
      setProfile(data)
    } catch (error: any) {
      toast.error(error.errors || 'Failed to load profile. Please try again later.')
    }
  }

  const closeConfirmAppointmentModal = () => {
    if (confirming) return

    setShowConfirmModal(false)
    loadDashboard()
    loadProfile()
  }

  const confirmAppointment = async () => {
    setConfirming(true)

    try {
      await updateAppointment(selectedAppointmentId, 'confirm')
    } catch (error: any) {
      toast.error(error.errors || 'Failed to confirm appointment. Please try again later.')
      throw error
    } finally {
      setConfirming(false)
    }
  }

  useEffect(() => {
    loadDashboard()
    loadProfile()
  }, [])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
      <div className="lg:col-span-2 space-y-4">
        {dashboardata ? (
          <>
            <Header
              appointments={upcomingAppointments}
              selectedId={selectedAppointmentId}
              onSelect={setSelectedAppointmentId}
            />

            <NextAppointment
              appointment={appointment}
              formsCompleted={arePatientFormsComplete(profile)}
              confirming={confirming}
              onConfirm={() => setShowConfirmModal(true)}
            />
          </>
        ) : (
          <VisitsSkeleton />
        )}
      </div>

      <div className="space-y-4">
        {profile ? <Profile profile={profile} onRefresh={loadProfile} /> : <ProfileSkeleton />}
      </div>

      {formsAccess ? (
        <ConfirmAppointmentModal
          open={showConfirmModal}
          appointment={appointment}
          profile={profile}
          loading={confirming}
          onClose={closeConfirmAppointmentModal}
          onConfirm={confirmAppointment}
        />
      ) : (
        <AppointmentModal
          open={showConfirmModal}
          loading={confirming}
          onClose={closeConfirmAppointmentModal}
          onConfirm={confirmAppointment}
        />
      )}
    </div>
  )
}

export default enhancer(Dashboard);
